import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCourse } from "../../../../context/CourseContext";
import { useUtilityContext } from "../../../../context/UtilityContext";
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Save,
  X,
} from "lucide-react";
import AttendanceSaveModal from "./AttendanceSaveModal";
import { useParams } from "react-router-dom";
import { updateCourse } from "../../../../services/course.service";
import toast from "react-hot-toast";
import {
  card,
  cardTitle,
  microLabel,
  bodySecondary,
  accentBarSection,
  dividerInner,
  surfaceMuted,
  badgeBase,
  motionFadeUp,
  motionStaggerChild,
  iconSize,
  pageTitle,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

// ---------------------------------------------------------------------------
// MAIN
// ---------------------------------------------------------------------------

const AttendanceTracker = () => {
  const {
    courseData,
    setCourseData,
    savedSessions,
    markStudentPresent,
    markStudentAbsent,
    getSessionAttendance,
    markSessionsAsSaved,
  } = useCourse();
  const { courseID: urlCourseID } = useParams();
  const { selectedCourseId } = useUtilityContext();
  const courseID = urlCourseID || selectedCourseId;

  const formatDateForDisplay = (dateString) => {
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (dateString.includes("/")) {
      const [day, month, year] = dateString.split("/");
      return `${year}-${month}-${day}`;
    }
    return dateString;
  };

  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [currentDate, setCurrentDate] = useState(getCurrentDate());
  const [currentTime, setCurrentTime] = useState(getCurrentTime());
  const [branchToggleStates, setBranchToggleStates] = useState({});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("all");
  const [selectedBranch, setSelectedBranch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  const uniquePrograms = useMemo(() => {
    if (!courseData?.students) return [];
    const programs = [
      ...new Set(courseData.students.map((s) => s.program).filter(Boolean)),
    ];
    return programs.sort();
  }, [courseData?.students]);

  const showProgramFilter = uniquePrograms.length > 1;

  const uniqueBranches = useMemo(() => {
    if (!courseData?.students) return [];
    let studentsToFilter = courseData.students;
    if (selectedProgram !== "all") {
      studentsToFilter = studentsToFilter.filter(
        (s) => s.program === selectedProgram
      );
    }
    const branches = [
      ...new Set(studentsToFilter.map((s) => s.branch).filter(Boolean)),
    ];
    return branches.sort();
  }, [courseData?.students, selectedProgram]);

  useEffect(() => {
    if (
      uniqueBranches.length > 0 &&
      (!selectedBranch || !uniqueBranches.includes(selectedBranch))
    ) {
      setSelectedBranch(uniqueBranches[0]);
    }
  }, [uniqueBranches, selectedBranch]);

  const showBranchSelector = uniqueBranches.length > 1;

  const filteredStudents = useMemo(() => {
    if (!courseData?.students) return [];
    const filtered = courseData.students.filter((student) => {
      const matchesSearch =
        searchTerm === "" ||
        student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.rollNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesProgram =
        selectedProgram === "all" || student.program === selectedProgram;
      const matchesBranch =
        !selectedBranch || student.branch === selectedBranch;
      return matchesSearch && matchesProgram && matchesBranch;
    });
    return filtered.sort((a, b) => {
      const rollA = a.rollNo || "";
      const rollB = b.rollNo || "";
      return rollA.localeCompare(rollB, undefined, { numeric: true });
    });
  }, [courseData?.students, searchTerm, selectedProgram, selectedBranch]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedProgram, selectedBranch, itemsPerPage]);

  const toggleAttendance = (studentId) => {
    const currentAttendance = getSessionAttendance(
      currentDate,
      currentTime,
      selectedBranch
    );
    if (currentAttendance.includes(studentId)) {
      markStudentAbsent(currentDate, currentTime, selectedBranch, studentId);
    } else {
      markStudentPresent(currentDate, currentTime, selectedBranch, studentId);
    }
  };

  const isStudentPresent = (studentId) => {
    const currentAttendance = getSessionAttendance(
      currentDate,
      currentTime,
      selectedBranch
    );
    return currentAttendance.includes(studentId);
  };

  const handleDateChange = (e) => {
    setCurrentDate(e.target.value);
  };

  const handleTimeChange = (e) => {
    setCurrentTime(e.target.value);
  };

  const markAllPresent = () => {
    filteredStudents.forEach((student) => {
      markStudentPresent(currentDate, currentTime, selectedBranch, student.id);
    });
    setBranchToggleStates({
      ...branchToggleStates,
      [selectedBranch]: true,
    });
  };

  const markAllAbsent = () => {
    filteredStudents.forEach((student) => {
      markStudentAbsent(currentDate, currentTime, selectedBranch, student.id);
    });
    setBranchToggleStates({
      ...branchToggleStates,
      [selectedBranch]: false,
    });
  };

  const calculateAttendancePercentage = () => {
    const totalStudents = filteredStudents.length;
    const sessionAttendance = getSessionAttendance(
      currentDate,
      currentTime,
      selectedBranch
    );
    const presentStudents = filteredStudents.filter((s) =>
      sessionAttendance.includes(s.id)
    ).length;
    return totalStudents > 0
      ? Math.round((presentStudents / totalStudents) * 100)
      : 0;
  };

  const getFilteredPresentCount = () => {
    const sessionAttendance = getSessionAttendance(
      currentDate,
      currentTime,
      selectedBranch
    );
    return filteredStudents.filter((s) =>
      sessionAttendance.includes(s.id)
    ).length;
  };

  // Previously saved sessions for this date (backend only)
  const savedTimesForDate = useMemo(() => {
    const sessions = Array.isArray(courseData?.attendance?.sessions)
      ? courseData.attendance.sessions
      : [];
    return sessions
      .filter((session) => {
        const sDate = (session.date || "").includes("T")
          ? session.date.split("T")[0]
          : session.date;
        const sessionKey = `${sDate}_${session.time || ""}_${session.branch || ""}`;
        return (
          sDate === currentDate &&
          (!selectedBranch || session.branch === selectedBranch) &&
          savedSessions?.has(sessionKey)
        );
      })
      .map((session) => session.time || "")
      .sort();
  }, [courseData?.attendance?.sessions, currentDate, selectedBranch, savedSessions]);

  // Current session + any duplicated sessions (unsaved/local only)
  const currentSessionTimes = useMemo(() => {
    const sessions = Array.isArray(courseData?.attendance?.sessions)
      ? courseData.attendance.sessions
      : [];
    const unsaved = sessions
      .filter((session) => {
        const sDate = (session.date || "").includes("T")
          ? session.date.split("T")[0]
          : session.date;
        const sessionKey = `${sDate}_${session.time || ""}_${session.branch || ""}`;
        return (
          sDate === currentDate &&
          (!selectedBranch || session.branch === selectedBranch) &&
          !savedSessions?.has(sessionKey)
        );
      })
      .map((session) => session.time || "");
    // Always include the current session time
    const times = new Set(unsaved);
    times.add(currentTime);
    return [...times].sort();
  }, [courseData?.attendance?.sessions, currentDate, selectedBranch, savedSessions, currentTime]);

  const isCurrentSessionDuplicate = useMemo(() => {
    const sessionKey = `${currentDate}_${currentTime}_${selectedBranch}`;
    return savedSessions?.has(sessionKey) || false;
  }, [savedSessions, currentDate, currentTime, selectedBranch]);

  const getAttendanceStats = () => {
    const sessionAttendance = getSessionAttendance(
      currentDate,
      currentTime,
      selectedBranch
    );
    const presentCount = filteredStudents.filter((s) =>
      sessionAttendance.includes(s.id)
    ).length;
    const absentCount = filteredStudents.length - presentCount;
    return {
      presentCount,
      absentCount,
      totalStudents: filteredStudents.length,
    };
  };

  const handleSaveClick = () => {
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    try {
      setIsSaving(true);
      setShowSaveModal(false);
      const updatedCourse = await updateCourse(courseID, courseData);
      setCourseData({ ...updatedCourse, students: courseData.students });
      const sessionsArr = Array.isArray(updatedCourse.attendance?.sessions)
        ? updatedCourse.attendance.sessions
        : [];
      const savedSessionKeys = sessionsArr.map((s) => {
        const dateStr = (s.date || "").includes("T")
          ? s.date.split("T")[0]
          : s.date;
        return `${dateStr}_${s.time || ""}_${s.branch || ""}`;
      });
      markSessionsAsSaved(savedSessionKeys);
      toast.success("Attendance saved successfully!");
    } catch (error) {
      console.error("Error saving attendance:", error);
      toast.error("Failed to save attendance");
    } finally {
      setIsSaving(false);
    }
  };

  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateDate, setDuplicateDate] = useState(currentDate);
  const [duplicateTime, setDuplicateTime] = useState("");

  const handleDuplicateAttendance = (targetDate, targetTime) => {
    if (!targetDate || !targetTime) {
      toast.error("Please select date and session time");
      return;
    }
    const currentAttendance = getSessionAttendance(
      currentDate,
      currentTime,
      selectedBranch
    );
    if (!currentAttendance || currentAttendance.length === 0) {
      toast.error("Mark attendance for current session first");
      return;
    }
    filteredStudents.forEach((student) => {
      if (currentAttendance.includes(student.id)) {
        markStudentPresent(targetDate, targetTime, selectedBranch, student.id);
      } else {
        markStudentAbsent(targetDate, targetTime, selectedBranch, student.id);
      }
    });
    toast.success(`Attendance added for ${targetDate} at ${targetTime}`);
  };

  const presentCount = getFilteredPresentCount();
  const absentCount = filteredStudents.length - presentCount;
  const allPresent = branchToggleStates[selectedBranch] === true;
  const allAbsent = branchToggleStates[selectedBranch] === false;

  return (
    <motion.div {...motionFadeUp} className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={pageTitle}>Attendance Tracker</h1>
          <p className={cn(bodySecondary, "mt-1")}>
            Track and manage student attendance records
          </p>
          <div className={cn(accentBarSection, "mt-3")} />
        </div>
        <button
          onClick={handleSaveClick}
          disabled={isSaving}
          className={cn(
            "flex items-center gap-2 bg-accent1 hover:bg-accent1/90 text-white py-2 px-4 rounded-sm transition-colors text-sm font-medium",
            isSaving && "opacity-70 cursor-not-allowed"
          )}
        >
          <Save className={cn(iconSize.md, isSaving && "animate-spin")} />
          {isSaving ? (
            <span>Saving...</span>
          ) : (
            <>
              <span className="hidden sm:inline">Save Changes</span>
              <span className="sm:hidden">Save</span>
            </>
          )}
        </button>
      </div>

      {/* Session Control Card */}
      <motion.div
        {...motionStaggerChild(0)}
        className={cn(card, "overflow-hidden")}
      >
        {/* Card Header */}
        <div
          className={cn(
            "px-5 py-4 flex items-center justify-between border-b",
            dividerInner
          )}
        >
          <h2 className={cardTitle}>Session Details</h2>
          {showBranchSelector && (
            <div className="relative">
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className={cn(
                  "pl-3 pr-8 py-1.5 border-2 border-accent1 rounded-sm bg-accent1/5 dark:bg-accent1/10 text-accent1 font-medium appearance-none cursor-pointer text-xs uppercase tracking-wider"
                )}
              >
                {uniqueBranches.map((branch) => (
                  <option key={branch} value={branch}>
                    {branch}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                <ChevronDown className={cn(iconSize.xs, "text-accent1")} />
              </div>
            </div>
          )}
        </div>

        {/* Card Body */}
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Date */}
            <div>
              <label className={cn(microLabel, "mb-2 block")}>
                Session Date
              </label>
              <div
                className={cn(
                  surfaceMuted,
                  "flex items-center gap-3 p-3 rounded-sm border",
                  dividerInner
                )}
              >
                <Calendar
                  className={cn(iconSize.md, "text-accent1 flex-shrink-0")}
                />
                <input
                  type="date"
                  value={formatDateForInput(currentDate)}
                  onChange={handleDateChange}
                  className="w-full bg-transparent focus:outline-none text-sm text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Time */}
            <div>
              <label className={cn(microLabel, "mb-2 block")}>
                Session Time
              </label>
              <div
                className={cn(
                  surfaceMuted,
                  "flex items-center gap-3 p-3 rounded-sm border",
                  dividerInner
                )}
              >
                <Clock
                  className={cn(iconSize.md, "text-accent1 flex-shrink-0")}
                />
                <input
                  type="time"
                  value={currentTime}
                  onChange={handleTimeChange}
                  className="w-full bg-transparent focus:outline-none text-sm text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Stats + Actions */}
            <div className="space-y-3">
              {/* Stats badges */}
              <div className="flex flex-wrap gap-2">
                <span
                  className={cn(
                    badgeBase,
                    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700"
                  )}
                >
                  {presentCount} Present
                </span>
                <span
                  className={cn(
                    badgeBase,
                    "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700"
                  )}
                >
                  {absentCount} Absent
                </span>
                <span
                  className={cn(
                    badgeBase,
                    "bg-accent1/10 text-accent1 border-accent1/30"
                  )}
                >
                  {calculateAttendancePercentage()}%
                </span>
              </div>

              {/* Mark All buttons */}
              <div className="flex gap-2">
                <button
                  onClick={markAllPresent}
                  className={cn(
                    "flex-1 py-2 px-3 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors border",
                    allPresent
                      ? "bg-emerald-600 text-white border-emerald-600 dark:bg-emerald-700 dark:border-emerald-700"
                      : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-700 dark:hover:bg-emerald-900/30"
                  )}
                >
                  All Present
                </button>
                <button
                  onClick={markAllAbsent}
                  className={cn(
                    "flex-1 py-2 px-3 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors border",
                    allAbsent
                      ? "bg-red-600 text-white border-red-600 dark:bg-red-700 dark:border-red-700"
                      : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/30"
                  )}
                >
                  All Absent
                </button>
              </div>
            </div>
          </div>

          {/* Duplicate warning */}
          {isCurrentSessionDuplicate && (
            <div className="flex items-center gap-3 mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-sm">
              <AlertTriangle
                className={cn(
                  iconSize.md,
                  "text-amber-600 dark:text-amber-400 flex-shrink-0"
                )}
              />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                This session was previously saved and will be updated.
              </p>
            </div>
          )}

          {/* Tip */}
          <p className={cn(bodySecondary, "mt-4")}>
            Tap a student's status to toggle between present and absent.
          </p>
        </div>
      </motion.div>

      {/* Student List Card */}
      <motion.div
        {...motionStaggerChild(1)}
        className={cn(card, "overflow-hidden")}
      >
        {/* Card Header */}
        <div
          className={cn(
            "px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b",
            dividerInner
          )}
        >
          <h2 className={cardTitle}>
            Student List{" "}
            <span className={cn(bodySecondary, "font-normal")}>
              ({filteredStudents.length})
            </span>
          </h2>

          <div className="flex flex-col sm:flex-row gap-2">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search
                  className={cn(iconSize.md, "text-gray-400 dark:text-gray-500")}
                />
              </div>
              <input
                type="text"
                placeholder="Search students..."
                className={cn(
                  "pl-9 pr-4 py-2 w-full sm:w-48 border rounded-sm focus:ring-2 focus:ring-accent1/50 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm",
                  dividerInner
                )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Program filter */}
            {showProgramFilter && (
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Filter
                    className={cn(
                      iconSize.md,
                      "text-gray-400 dark:text-gray-500"
                    )}
                  />
                </div>
                <select
                  value={selectedProgram}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                  className={cn(
                    "pl-9 pr-8 py-2 border rounded-sm focus:ring-2 focus:ring-accent1/50 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none cursor-pointer text-sm min-w-[130px]",
                    dividerInner
                  )}
                >
                  <option value="all">All Programs</option>
                  {uniquePrograms.map((program) => (
                    <option key={program} value={program}>
                      {program}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                  <ChevronDown
                    className={cn(
                      iconSize.md,
                      "text-gray-400 dark:text-gray-500"
                    )}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr
                className={cn(
                  surfaceMuted,
                  "border-b",
                  dividerInner
                )}
              >
                <th
                  className={cn(
                    microLabel,
                    "hidden md:table-cell py-3 px-5 text-left"
                  )}
                >
                  #
                </th>
                <th
                  className={cn(
                    microLabel,
                    "hidden md:table-cell py-3 px-5 text-left"
                  )}
                >
                  Roll No.
                </th>
                <th
                  className={cn(
                    microLabel,
                    "hidden md:table-cell py-3 px-5 text-left"
                  )}
                >
                  Name
                </th>
                <th
                  className={cn(
                    microLabel,
                    "md:hidden py-3 px-4 text-left"
                  )}
                >
                  Student
                </th>
                <th
                  className={cn(microLabel, "py-3 px-4 md:px-5 text-center")}
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {currentStudents.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    className={cn(
                      bodySecondary,
                      "py-8 text-center"
                    )}
                  >
                    No students found matching the filters
                  </td>
                </tr>
              ) : (
                currentStudents.map((student, index) => {
                  const present = isStudentPresent(student.id);
                  return (
                    <tr
                      key={student.id}
                      className={cn(
                        "border-b hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150",
                        dividerInner
                      )}
                    >
                      {/* Desktop */}
                      <td className="hidden md:table-cell py-3.5 px-5 text-sm text-gray-500 dark:text-gray-400">
                        {indexOfFirstItem + index + 1}
                      </td>
                      <td className="hidden md:table-cell py-3.5 px-5 text-sm font-medium text-gray-900 dark:text-white">
                        {student.rollNo || "—"}
                      </td>
                      <td className="hidden md:table-cell py-3.5 px-5 text-sm text-gray-700 dark:text-gray-200">
                        {student.name}
                      </td>

                      {/* Mobile */}
                      <td className="md:hidden py-3 px-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {student.rollNo || "—"}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {student.name}
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4 md:px-5">
                        <div className="flex justify-center">
                          <button
                            onClick={() => toggleAttendance(student.id)}
                            className={cn(
                              badgeBase,
                              "inline-flex items-center gap-1.5 cursor-pointer transition-colors",
                              present
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700 dark:hover:bg-emerald-900/40"
                                : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/40"
                            )}
                          >
                            {present ? (
                              <>
                                <CheckCircle className={iconSize.md} />
                                <span className="text-xs font-medium">
                                  Present
                                </span>
                              </>
                            ) : (
                              <>
                                <XCircle className={iconSize.md} />
                                <span className="text-xs font-medium">
                                  Absent
                                </span>
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredStudents.length > 0 && (
          <div
            className={cn(
              "p-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t",
              dividerInner
            )}
          >
            <div className="flex items-center gap-4">
              <span className={bodySecondary}>
                {indexOfFirstItem + 1}–
                {Math.min(indexOfLastItem, filteredStudents.length)} of{" "}
                {filteredStudents.length}
              </span>
              <div className="flex items-center gap-2">
                <span className={bodySecondary}>Show:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className={cn(
                    surfaceMuted,
                    "px-2 py-1 border rounded-sm text-gray-900 dark:text-white text-sm cursor-pointer",
                    dividerInner
                  )}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.max(prev - 1, 1))
                }
                disabled={currentPage === 1}
                className={cn(
                  "p-2 rounded-sm border hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300",
                  dividerInner
                )}
              >
                <ChevronLeft className={iconSize.md} />
              </button>
              <span className={cn(bodySecondary, "px-2")}>
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages || totalPages === 0}
                className={cn(
                  "p-2 rounded-sm border hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300",
                  dividerInner
                )}
              >
                <ChevronRight className={iconSize.md} />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Attendance Save Modal */}
      <AttendanceSaveModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onConfirm={handleConfirmSave}
        onDuplicate={() => {
          setShowSaveModal(false);
          setShowDuplicateModal(true);
        }}
        currentDate={currentDate}
        currentTime={currentTime}
        branch={selectedBranch}
        presentCount={getAttendanceStats().presentCount}
        absentCount={getAttendanceStats().absentCount}
        totalStudents={getAttendanceStats().totalStudents}
        savedTimes={savedTimesForDate}
        currentSessionTimes={currentSessionTimes}
        isDuplicate={isCurrentSessionDuplicate}
      />

      {/* Duplicate Session Modal — portaled to body */}
      {showDuplicateModal && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[9999]">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => {
              setShowDuplicateModal(false);
              setShowSaveModal(true);
            }}
          />
          <motion.div
            {...motionFadeUp}
            className={cn(card, "relative w-full max-w-md mx-4 p-6 space-y-4 shadow-xl")}
          >
            <div className="flex items-center justify-between">
              <h3 className={cardTitle}>Select Session to Add</h3>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setShowSaveModal(true);
                }}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors"
              >
                <X className={iconSize.lg} />
              </button>
            </div>

            <div>
              <label className={cn(microLabel, "mb-2 block")}>Date</label>
              <input
                type="date"
                value={duplicateDate}
                onChange={(e) => setDuplicateDate(e.target.value)}
                className={cn(
                  surfaceMuted,
                  "w-full border rounded-sm p-2.5 text-sm text-gray-900 dark:text-white",
                  dividerInner
                )}
              />
            </div>

            <div>
              <label className={cn(microLabel, "mb-2 block")}>
                Session Time
              </label>
              <input
                type="time"
                value={duplicateTime}
                onChange={(e) => setDuplicateTime(e.target.value)}
                className={cn(
                  surfaceMuted,
                  "w-full border rounded-sm p-2.5 text-sm text-gray-900 dark:text-white",
                  dividerInner
                )}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setShowSaveModal(true);
                }}
                className={cn(
                  "px-4 py-2.5 text-sm font-medium border rounded-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                  dividerInner
                )}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDuplicateAttendance(duplicateDate, duplicateTime);
                  setShowDuplicateModal(false);
                  setShowSaveModal(true);
                }}
                className="px-4 py-2.5 text-sm font-medium bg-accent1 hover:bg-accent1/90 text-white rounded-sm transition-colors"
              >
                Add
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </motion.div>
  );
};

export default AttendanceTracker;
